import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
} from '@react-pdf/renderer';
const styles = StyleSheet.create({
  page: {
    padding: 15,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  headerRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: 20,
      padding: 20
  },
  title: {
    fontSize: 20,
    fontweight: 'bold',
    marginBottom: 10,
  },
  buyerBlock: {
    maxWidth: 180,
  },
  meta: {
    textAlign: 'right',
    color: '#374151',
  },
  metaBlock: {
    display: 'flex',
    paddingLeft: 50,
    marginBottom: 4,
  },
  metaLabel: {
    fontWeight: 'bold',
    fontSize: 10,
    color: '#374151',
  },
  metaValue: {
    fontSize: 10,
    color: '#374151',
    marginBottom: 2,
  },
  text: {
    fontSize: 10,
    color: '#374151',
    marginBottom: 2,
  },
  supplierBlock: {
    maxWidth: 180,
    alignItems: 'flex-end',
  },
  table: {
    marginTop: 16,
    marginBottom: 24,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    paddingVertical: 5,
    paddingHorizontal: 4,
    fontSize: 8,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f9fafb',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    paddingVertical: 4,
    paddingHorizontal: 4,
  },
  colDesc: { width: '35%', paddingRight: 8 },
  colQty: { width: '12%', textAlign: 'right', paddingRight: 12 },
  colRate: { width: '18%', textAlign: 'right', paddingRight: 12 },
  colGst: { width: '12%', textAlign: 'right', paddingRight: 10, paddingLeft: 12 },
  colAmount: { width: '15%', textAlign: 'right', paddingLeft: 10 },
  headerText: {
    fontWeight: 'bold',
    color: '#374151',
  },
  totalsWrap: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: 16,
  },
  totalsBox: {
    width: 180,
  },
  totalsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  totalsDivider: {
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    paddingTop: 8,
    marginTop: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  totalFinal: {
    borderTopWidth: 2,
    borderTopColor: '#1f2937',
    paddingTop: 8,
    marginTop: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontWeight: 'bold',
  },
  deliverySection: {
    marginTop: 24,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  deliveryTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  deliveryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  deliveryCol: {
    flex: 1,
    paddingRight: 16,
  },
  deliveryColLast: {
    flex: 1,
  },
  deliveryLabel: {
    fontWeight: 'bold',
    fontSize: 9,
    color: '#374151',
    marginBottom: 2,
  },
  pageNum: {
    position: 'absolute',
    bottom: 12,
    left: 20,
    fontSize: 8,
    color: '#6b7280',
  },
});
function getTaxRatePercent(lineItem, taxes = []) {
  if (!lineItem?.tax_rate_id) return null;
  const tax = taxes.find((t) => t.tax_id === lineItem.tax_rate_id);
  if (tax) return parseFloat(tax.tax_rate);
  if (lineItem.tax_rate_id === 'GST') return 10;
  return null;
}
function formatGstRate(ratePercent) {
  if (ratePercent == null) return '—';
  return `${Number(ratePercent)}%`;
}
function getTotalGstLabel(formData, lineItems = [], taxes = []) {
  if (formData?.tax_inclusion === 'no_tax') return null;
  const firstWithTax = lineItems.find((li) => li.tax_rate_id);
  const rate = firstWithTax != null ? getTaxRatePercent(firstWithTax, taxes) : null;
  if (rate != null) return `TOTAL GST ${rate}%`;
  return 'TOTAL TAX';
}

export default function PurchasePdfDocument({ draftPurchase }) {
  if (!draftPurchase) return null;
  const { formData, lineItems = [], subtotal, taxTotal, grandTotal, taxes = [], company, supplier } = draftPurchase;
  const showGst = formData?.tax_inclusion !== 'no_tax';
  const totalGstLabel = showGst ? getTotalGstLabel(formData, lineItems, taxes) : null;
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    if (Number.isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}-${month}-${d.getFullYear()}`;
  };
  const supplierAddress = supplier && [supplier.address || supplier.street, supplier.city, supplier.state, supplier.postcode].filter(Boolean).join(', ');
  const hasDeliveryDetails = formData.ship_to || formData.attention || formData.contact_name || formData.telephone || formData.delivery_instructions || formData.note_to_supplier;
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Title */}
          <Text style={styles.title}>PURCHASE ORDER</Text>
          <View style={styles.headerRow}>
          <View style={styles.buyerBlock}>
            <Text style={[styles.text, { color: '#111827' }]}>{company?.companyname || '—'}</Text>
            {company?.street && <Text style={styles.text}>{company.street}</Text>}
            {(company?.city || company?.state || company?.postcode) && (
              <Text style={styles.text}>
                {[company.city, company.state, company.postcode].filter(Boolean).join(' ')}
              </Text>
            )}
            {company?.country && <Text style={styles.text}>{company.country}</Text>}
            {company?.abn && <Text style={[styles.text, { marginTop: 4 }]}>ABN: {company.abn}</Text>}
          </View>
          <View style={styles.meta}>
            <View style={styles.metaBlock}>
              <Text style={styles.metaLabel}>Purchase Order Date</Text>
              <Text style={styles.metaValue}>{formatDate(formData.purchase_order_date)}</Text>
            </View>
            <View style={styles.metaBlock}>
              <Text style={styles.metaLabel}>Delivery Date</Text>
              <Text style={styles.metaValue}>{formatDate(formData.due_date || formData.delivery_date)}</Text>
            </View>
            <View style={styles.metaBlock}>
              <Text style={styles.metaLabel}>Purchase Order Number</Text>
              <Text style={styles.metaValue}>{formData.purchase_number || '—'}</Text>
            </View>
            <View style={styles.metaBlock}>
              <Text style={styles.metaLabel}>ABN</Text>
              <Text style={styles.metaValue}>{company?.abn || company?.abnno || '—'}</Text>
            </View>
          </View>
          <View style={styles.supplierBlock}>
            <Text style={[styles.text, { textAlign: 'right' }]}>{formData.supplier_name || '—'}</Text>
            {supplierAddress && <Text style={[styles.text, { textAlign: 'right' }]}>{supplierAddress}</Text>}
            {supplier?.country && <Text style={[styles.text, { textAlign: 'right' }]}>{supplier.country}</Text>}
          </View>
        </View>
        {/* Line Items Table - same columns as invoice (Description, Qty, Unit Price, GST, Amount) */}
        <View style={styles.table}>
          <View fixed style={styles.tableHeader}>
            <Text style={[styles.colDesc, styles.headerText]}>Description</Text>
            <Text style={[styles.colQty, styles.headerText]}>Quantity</Text>
            <Text style={[styles.colRate, styles.headerText]}>Unit Price</Text>
            {showGst && <Text style={[styles.colGst, styles.headerText]}>GST</Text>}
            <Text style={[styles.colAmount, styles.headerText]}>Amount AUD</Text>
          </View>
          {(lineItems || []).map((li, idx) => {
            const ratePercent = getTaxRatePercent(li, taxes);
            const gstDisplay = showGst ? formatGstRate(ratePercent) : null;
            return (
              <View key={li.id || idx} style={styles.tableRow}>
                <Text style={styles.colDesc}>{li.item_description || ''}</Text>
                <Text style={styles.colQty}>{Number(li.quantity ?? 0).toFixed(2)}</Text>
                <Text style={styles.colRate}>{Number(li.unit_price || 0).toFixed(2)}</Text>
                {showGst && <Text style={styles.colGst}>{gstDisplay}</Text>}
                <Text style={styles.colAmount}>{Number(li.total_price || 0).toLocaleString('en-AU', { maximumFractionDigits: 2 })}</Text>
              </View>
            );
          })}
        </View>
        {/* Totals - Subtotal, TOTAL GST, TOTAL with separator lines */}
        <View style={styles.totalsWrap} wrap={false}>
          <View style={styles.totalsBox}>
            <View style={styles.totalsRow}>
              <Text>Subtotal</Text>
              <Text>{Number(subtotal || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
            </View>
            {totalGstLabel != null && (
              <View style={styles.totalsRow}>
                <Text>{totalGstLabel}</Text>
                <Text>{Number(taxTotal || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
              </View>
            )}
            <View style={styles.totalFinal}>
              <Text style={{ fontWeight: 'bold', fontSize: 11 }}>TOTAL AUD</Text>
              <Text style={{ fontWeight: 'bold', fontSize: 11 }}>{Number(grandTotal || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>
            </View>
          </View>
        </View>
        {/* Delivery Details Section */}
        {hasDeliveryDetails && (
          <View style={styles.deliverySection} wrap={false}>
            <Text style={styles.deliveryTitle}>DELIVERY DETAILS</Text>
            <View style={styles.deliveryRow}>
              <View style={styles.deliveryCol}>
                <Text style={styles.deliveryLabel}>Delivery Address</Text>
                <Text style={styles.text}>{formData.ship_to || '—'}</Text>
              </View>
              <View style={styles.deliveryCol}>
                <Text style={styles.deliveryLabel}>Attention</Text>
                <Text style={styles.text}>{formData.contact_name || formData.attention || '—'}</Text>
                <Text style={[styles.deliveryLabel, { marginTop: 6 }]}>Telephone</Text>
                <Text style={styles.text}>{formData.telephone || '—'}</Text>
                {formData.email && (
                  <>
                    <Text style={[styles.deliveryLabel, { marginTop: 6 }]}>Email</Text>
                    <Text style={styles.text}>{formData.email || '—'}</Text>
                  </>
                )}
              </View>
              <View style={styles.deliveryColLast}>
                <Text style={styles.deliveryLabel}>Delivery Instructions</Text>
                <Text style={styles.text}>{formData.note_to_supplier || formData.delivery_instructions || '—'}</Text>
              </View>
            </View>
          </View>
        )}
        {/* Page number */}
        <Text
          style={styles.pageNum}
          fixed
          render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
        />
        <Text
          style={{
            position: 'absolute',
            bottom: 12,
            left: 0,
            right: 0,
            textAlign: 'center',
            fontSize: 8,
            color: '#9ca3af',
          }}
          fixed
        >
          Powered by runbusiness.io
        </Text>
      </Page>
    </Document>
  );
}